import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Button, Tabs, Table, Tag, Spin, Typography, Space, Divider } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { EditOutlined, ArrowLeftOutlined, ShoppingCartOutlined, CommentOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { Store, PurchaseOrder, ReviewTask } from '../../types';

const { Title } = Typography;
const { TabPane } = Tabs;

const StoreDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState<Store | null>(null);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [reviewTasks, setReviewTasks] = useState<ReviewTask[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 獲取店鋪詳情
        const storeData = await api.stores.getById(id as string);
        setStore(storeData);

        // 獲取店鋪的採購訂單
        const ordersData = await api.stores.getPurchaseOrders(id as string);
        setPurchaseOrders(ordersData);

        // 獲取店鋪的測評任務
        const tasksData = await api.stores.getReviewTasks(id as string);
        setReviewTasks(tasksData);
      } catch (error) {
        console.error('Error fetching store details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // 根據店鋪類型獲取標籤顏色
  const getTypeColor = (type: string): string => {
    switch (type) {
      case '自用':
        return 'blue';
      case '月租':
        return 'green';
      case '季度':
        return 'purple';
      default:
        return 'default';
    }
  };

  // 根據店鋪狀態獲取標籤顏色
  const getStatusColor = (status: string): string => {
    switch (status) {
      case '營業中':
        return 'success';
      case '停業':
        return 'warning';
      case '失效':
        return 'error';
      default:
        return 'default';
    }
  };

  // 採購訂單表格列
  const orderColumns: ColumnType<PurchaseOrder>[] = [
    {
      title: t('purchaseOrders.orderId'),
      dataIndex: 'order_id',
      key: 'order_id',
    },
    {
      title: t('purchaseOrders.platform'),
      dataIndex: 'platform',
      key: 'platform',
    },
    {
      title: t('purchaseOrders.purchaseDate'),
      dataIndex: 'purchase_date',
      key: 'purchase_date',
    },
    {
      title: t('purchaseOrders.totalAmount'),
      dataIndex: 'total_amount',
      key: 'total_amount',
    },
    {
      title: t('purchaseOrders.courierStatus'),
      dataIndex: 'courier_status',
      key: 'courier_status',
      render: (status: string) => (
        <Tag color={status === '已填寫' ? 'success' : 'warning'}>
          {status}
        </Tag>
      ),
    },
    {
      title: t('common.action'),
      key: 'action',
      render: (_: any, record: PurchaseOrder) => (
        <Button
          type="link"
          onClick={() => navigate(`/purchase-orders/${record.id}`)}
        >
          {t('common.edit')}
        </Button>
      ),
    },
  ];

  // 測評任務表格列
  const taskColumns: ColumnType<ReviewTask>[] = [
    {
      title: t('reviewTasks.taskId'),
      dataIndex: 'task_id',
      key: 'task_id',
    },
    {
      title: t('reviewTasks.platform'),
      dataIndex: 'platform',
      key: 'platform',
    },
    {
      title: t('reviewTasks.date'),
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: t('reviewTasks.amount'),
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: t('reviewTasks.reviewStatus'),
      dataIndex: 'review_status',
      key: 'review_status',
      render: (status: string) => (
        <Tag color={status === '已評論' ? 'success' : 'warning'}>
          {status}
        </Tag>
      ),
    },
    {
      title: t('reviewTasks.deliveryStatus'),
      dataIndex: 'delivery_status',
      key: 'delivery_status',
      render: (status: string) => (
        <Tag color={status === '已收貨' ? 'success' : 'warning'}>
          {status}
        </Tag>
      ),
    },
    {
      title: t('common.action'),
      key: 'action',
      render: (_: any, record: ReviewTask) => (
        <Button
          type="link"
          onClick={() => navigate(`/review-tasks/${record.id}`)}
        >
          {t('common.edit')}
        </Button>
      ),
    },
  ];

  if (loading) {
    return <Spin size="large" />;
  }

  if (!store) {
    return <div>{t('common.noData')}</div>;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/stores')}
        >
          {t('common.back')}
        </Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/stores/${id}/edit`)}
        >
          {t('common.edit')}
        </Button>
      </Space>

      <Title level={2}>{store.name}</Title>

      <Card style={{ marginBottom: 24 }}>
        <Descriptions title={t('stores.title')} bordered>
          <Descriptions.Item label={t('stores.storeId')}>{store.store_id}</Descriptions.Item>
          <Descriptions.Item label={t('stores.platform')}>{store.platform}</Descriptions.Item>
          <Descriptions.Item label={t('stores.shortName')}>{store.short_name}</Descriptions.Item>
          <Descriptions.Item label={t('stores.type')}>
            <Tag color={getTypeColor(store.type)}>{store.type}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('stores.status')}>
            <Tag color={getStatusColor(store.status)}>{store.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('stores.owner')}>{store.owner}</Descriptions.Item>

          {(store.type === '月租' || store.type === '季度') && (
            <>
              <Descriptions.Item label={t('stores.rentalAmount')}>{store.rental_amount}</Descriptions.Item>
              <Descriptions.Item label={t('stores.rentalStartDate')}>
                {typeof store.rental_start_date === 'string'
                  ? store.rental_start_date
                  : store.rental_start_date?.format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label={t('stores.rentalEndDate')}>
                {typeof store.rental_end_date === 'string'
                  ? store.rental_end_date
                  : store.rental_end_date?.format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label={t('stores.rentalPaymentStatus')}>
                <Tag color={store.rental_payment_status === '已支付' ? 'success' : 'error'}>
                  {store.rental_payment_status}
                </Tag>
              </Descriptions.Item>
              {store.rental_payment_status === '已支付' && (
                <Descriptions.Item label={t('stores.rentalPaymentDate')}>
                  {typeof store.rental_payment_date === 'string'
                    ? store.rental_payment_date
                    : store.rental_payment_date?.format('YYYY-MM-DD')}
                </Descriptions.Item>
              )}
            </>
          )}

          <Descriptions.Item label={t('stores.createdAt')}>{store.created_at}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs defaultActiveKey="orders">
        <TabPane
          tab={
            <span>
              <ShoppingCartOutlined />
              {t('stores.purchaseOrders')}
            </span>
          }
          key="orders"
        >
          <Table
            columns={orderColumns}
            dataSource={purchaseOrders}
            rowKey="id"
            pagination={{ pageSize: 5 }}
          />
        </TabPane>
        <TabPane
          tab={
            <span>
              <CommentOutlined />
              {t('stores.reviewTasks')}
            </span>
          }
          key="tasks"
        >
          <Table
            columns={taskColumns}
            dataSource={reviewTasks}
            rowKey="id"
            pagination={{ pageSize: 5 }}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default StoreDetail;
